/**
 * AI calls, the results (`/ai-calls`): every call the AI placed, and what came of it.
 *
 * Ring R2 of the AI calling plan: the result pass writes one word per call
 * (meeting, call back, not interested, no answer...) with a summary and the
 * next step, within a minute of the call ending. This page is where those
 * results are read. The campaigns (R3) are the section's other tab; the
 * automation card joins them in a later ring.
 *
 * Newest first, filtered by test or real calls and by the word, with the count
 * per word beside each filter option, and a "show more" that continues from
 * the last row rather than from a page number that shifts as calls arrive.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Loader2, RefreshCw } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { StaffUser } from "../../../../lib/auth";
import {
  OUTBOUND_DISPOSITIONS,
  RESULT_MODES,
  getAiOutboundResults,
  mergeResultPages,
  totalResults,
  type AiOutboundResults,
  type ResultMode,
} from "../../../../lib/aiOutboundResults";
import { Btn, EmptyState, PageHead, PillTabs, SelectField, Toolbar, ToolbarGap, fmtNum } from "../../../ui";
import { AiCallsTabs } from "../AiCallsTabs";
import { ResultRow } from "./ResultRow";

export function AiCallResultsPage(_props: { user: StaffUser }) {
  const t = useT();
  const [mode, setMode] = useState<ResultMode>("all");
  const [disposition, setDisposition] = useState("");
  const [data, setData] = useState<AiOutboundResults | null>(null);
  const [error, setError] = useState(false);
  const [more, setMore] = useState(false);
  const request = useRef(0);

  const load = useCallback(async () => {
    const id = ++request.current;
    setError(false);
    try {
      const next = await getAiOutboundResults({ mode, disposition });
      if (id === request.current) setData(next);
    } catch {
      if (id === request.current) {
        setData(null);
        setError(true);
      }
    }
  }, [mode, disposition]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  async function loadMore() {
    if (!data?.nextBefore || more) return;
    setMore(true);
    const id = request.current;
    try {
      const next = await getAiOutboundResults({ mode, disposition }, data.nextBefore);
      if (id === request.current) {
        setData((prev) => (prev ? { ...next, rows: mergeResultPages(prev.rows, next.rows) } : next));
      }
    } catch {
      setError(true);
    } finally {
      setMore(false);
    }
  }

  const counts = data?.counts ?? {};
  const pending = data?.pending ?? 0;
  const modeTabs = RESULT_MODES.map((value) => ({ value, label: t(`aiCalls.mode.${value}`) }));

  return (
    <div>
      <PageHead
        title={t("aiCalls.title")}
        sub={t("aiCalls.intro")}
        actions={
          <Btn onClick={() => void load()} disabled={data === null && !error}>
            <RefreshCw className="size-3.5" />
            {t("common.refresh")}
          </Btn>
        }
      />

      <AiCallsTabs current="results" />

      <Toolbar>
        <PillTabs tabs={modeTabs} value={mode} onChange={setMode} small />
        <ToolbarGap />
        <SelectField value={disposition} onChange={setDisposition} label={t("aiCalls.filterLabel")}>
          <option value="">{t("aiCalls.filterAll", { count: fmtNum(totalResults(counts, pending)) })}</option>
          <option value="pending">{t("aiCalls.filterOption", { label: t("aiCalls.disposition.pending"), count: fmtNum(pending) })}</option>
          {OUTBOUND_DISPOSITIONS.map((word) => (
            <option key={word} value={word}>
              {t("aiCalls.filterOption", { label: t(`aiCalls.disposition.${word}`), count: fmtNum(counts[word] ?? 0) })}
            </option>
          ))}
        </SelectField>
      </Toolbar>

      <div className="crm-card overflow-hidden p-0">
        {data === null && !error ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : error && !data ? (
          <EmptyState icon={Bot} title={t("aiCalls.errorTitle")} body={t("aiCalls.errorBody")} />
        ) : data && data.rows.length === 0 ? (
          <EmptyState icon={Bot} title={t("aiCalls.emptyTitle")} body={t("aiCalls.emptyBody")} />
        ) : data ? (
          <>
            <ul className="divide-y divide-border">
              {data.rows.map((row) => (
                <ResultRow key={row.id} row={row} />
              ))}
            </ul>
            {data.nextBefore ? (
              <div className="flex justify-center border-t border-border p-3">
                <Btn onClick={() => void loadMore()} disabled={more}>
                  {more ? <Loader2 className="size-3.5 animate-spin" /> : null}
                  {t("aiCalls.more")}
                </Btn>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
